// ** React Imports
import { useRef } from "react";
import { useLocation, matchPath } from "react-router-dom";

// ** Redux & Store & Actions
import { useSelector, useDispatch } from "react-redux";
import { getGallery } from "store/modules/main-pages/galleries/get-gallery.action";
import { getPosts } from "store/modules/main-pages/posts/get-posts.action";
import { getRankings } from "store/modules/main-pages/rankings/get-ranking.action";

// ** Third Party Components
import loadable from "@loadable/component";
import { useTranslation } from "react-i18next";

// ** Custom Components
import Block from "components/layout/Block";
import Text from "components/typography/Text";
import UserAvatar from "components/user/UserAvatar";
import VipStatus from "components/user/VipStatus";
import ChangeStatus from "components/user/ChangeStatus";
import Spacer from "components/layout/Spacer";
import Login from "components/user/Login";
import { CardBody } from "components/layout/Card/CardBody";
import { Icon } from "components/icons";
import { StyledSideMenuDivider } from "./SideMenuDivider.styled";
import { SideMenuServicesSection } from "./SideMenuServicesSection";

// ** Custom Hooks
import useLogout from "hooks/useLogout";
import { compute as filterSexCompute } from "hooks/filters/useFilterSex";

// ** Utils & Helpers
import { searchSexCustomSort } from "utils/searchSexCustomSort";
import pseudoFormat from "utils/pseudoFormat";
import generateQueryPath from "utils/generateQueryPath";
import { VIP_STATUS } from "helpers/constants";

// ** Routes
import { routesArray as userpanelRoutesArray } from "containers/Userpanel";

// ** Styled Components
import {
	StyledNavigationMenuListItemLink,
	StyledNavigationMenuListItem,
	StyledNavigationMenuListItemIcon,
	StyledSideMenuCard,
	StyledSideFixed,
} from "./styles";

// ** Lazy Loading Imports
const Menu = loadable(() => import("components/layout/Menu"), { ssr: false });

// ** Icons
import {
	UserCircleSolidIcon,
	UserCircleRegularIcon,
} from "components/icons/UserCircle.icons";
import {
	PhotoVideoSolidIcon,
	PhotoVideoRegularIcon,
} from "components/icons/PhotoVideo.icons";
import { ImageSolidIcon, ImageRegularIcon } from "components/icons/Image.icons";
import {
	AlignLeftSolidIcon,
	AlignLeftRegularIcon,
} from "components/icons/AlignLeft.icons";
import {
	FilmAltSolidIcon,
	FilmAltRegularIcon,
} from "components/icons/FilmAlt.icons";
import {
	UserFriendsSolidIcon,
	UserFriendsRegularIcon,
} from "components/icons/UserFriends.icons";
import {
	TrophySolidIcon,
	TrophyRegularIcon,
} from "components/icons/Trophy.icons";
import {
	LocationArrowSolidIcon,
	LocationArrowRegularIcon,
} from "components/icons/LocationArrow.icons";

const svgIconsList = {
	// Użytkownicy
	["user-circle"]: UserCircleSolidIcon,
	["user-circle-regular"]: UserCircleRegularIcon,
	// Galeria
	["photo-video-solid"]: PhotoVideoSolidIcon,
	["photo-video-regular"]: PhotoVideoRegularIcon,
	// Zdjęcia
	["image-solid"]: ImageSolidIcon,
	["image-regular"]: ImageRegularIcon,
	// Filmy
	["film-alt-regular"]: FilmAltRegularIcon,
	["film-alt-solid"]: FilmAltSolidIcon,
	// Posty
	["align-left"]: AlignLeftSolidIcon,
	["align-left-regular"]: AlignLeftRegularIcon,
	// Aktywność
	["user-friends"]: UserFriendsSolidIcon,
	["user-friends-regular"]: UserFriendsRegularIcon,
	// W okolicy
	["location-arrow-solid"]: LocationArrowSolidIcon,
	["location-arrow-regular"]: LocationArrowRegularIcon,
	// Rankingi
	["trophy"]: TrophySolidIcon,
	["trophy-regular"]: TrophyRegularIcon,
};

// #####################################################

const NavigationMenuListLink = ({ children, ...rest }) => (
	<StyledNavigationMenuListItemLink
		container={StyledNavigationMenuListItem}
		{...rest}
	>
		{children}
	</StyledNavigationMenuListItemLink>
);

// #####################################################

const SideMenu = ({ mainPageNavItems }) => {
	const { t } = useTranslation(["pages"]);
	const dispatch = useDispatch();

	const isAuth = useSelector((state) => state.globalCommon.isAuth);
	const isClient = useSelector((state) => state.globalCommon.isClient);

	const user = useSelector((state) => state.globalUser);
	const statisticsSelf = useSelector((state) => state.global.statisticsSelf);

	const logoutFn = useLogout();

	const location = useLocation();
	const isUserpanel = location.pathname.startsWith("/userpanel");

	const containerRef = useRef(null);
	const contentRef = useRef(null);

	const finalUserpanelRoutesArray = user.frozen
		? userpanelRoutesArray.filter(({ meta }) => meta.suspended)
		: userpanelRoutesArray;

	const userCityId = useSelector((state) => state.globalUser.city);
	const userRegionId = useSelector((state) => state.globalUser.region);
	const userCountryCode = useSelector((state) => state.globalUser.country);
	const userPrefferedSex = useSelector(
		(state) => state.globalUser.searchSex,
	).join("-");
	const userUrlSearch = useSelector((state) => state.globalUser.urlSearch);
	const userUrlSearchCountry = userUrlSearch
		? userUrlSearch.split("/")[0]
		: null;

	const handleGetSharedGallery = (siteName) => {
		dispatch(getGallery({ siteName }));
	};

	const handleGetPosts = () => {
		dispatch(
			getPosts({
				isAuth,
			}),
		);
	};

	const handleGetRankings = () => {
		dispatch(
			getRankings({
				apiParams: {
					period: 0,
					country: userUrlSearchCountry || null,
					region: null,
					sex: filterSexCompute(true).urlToApiParam(
						isAuth ? userPrefferedSex : "any",
					),
					page: 1,
				},
				useUrl: true,
			}),
		);
	};

	const profileMenuItems = [
		{
			key: "myProfile",
			content: ({ dropdownContainer }) => (
				<Block
					flex
					alignCenter
					style={{ padding: "0 0.5rem 0.5rem" }}
					key="myProfile"
				>
					<UserAvatar
						noOnlineCircle
						userId={user?.id}
						borderSize
						src={user.avatar}
						login={user.login}
						privateData={user.private_data || user.privateData}
						size="xsmall"
						link
					/>
					<Block flex column ml={2}>
						<Login
							login={user.login}
							vip={
								user?.showStatusVip
									? user.status_vip
									: user.status_vip > VIP_STATUS.VERIFIED
										? VIP_STATUS.VERIFIED
										: user.status_vip
							}
							link
							maxWidthWithEllipsis={150}
							vipProps={{ height: 14, marginBottom: 0.125 }}
						/>
						<ChangeStatus dropdownContainer={dropdownContainer} />
					</Block>
				</Block>
			),
		},
		{
			key: "d1",
			content: () => <StyledSideMenuDivider />,
		},
		...finalUserpanelRoutesArray
			.sort((a, b) => {
				if (a.meta.navIndex > b.meta.navIndex) return 1;
				if (a.meta.navIndex < b.meta.navIndex) return -1;
				return 0;
			})
			.map((route, index) => ({
				key: `${route.key}-${index}`,
				color: "black",
				wrap: route.meta?.wrap,
				content: t(
					(route.meta && route.meta.titleTransKey) || route.key,
				),
				link: {
					to: route.link,
					exact: route.exact,
				},
				append: () => {
					if (route.meta && route.meta.menuPillStatKey) {
						const value = Number(
							statisticsSelf[route.meta.menuPillStatKey],
						);

						if (route.meta.menuPillStatKey === "media") {
							return (
								<>
									{value > 0 && (
										<Block bold ml={2}>
											{pseudoFormat(value)}{" "}
										</Block>
									)}
									<Block flex ml={2}>
										<Icon
											name="plus-circle"
											height="1rem"
											variant="light"
										/>
									</Block>
								</>
							);
						}

						if (route.meta.menuPillStatKey === "friends") {
							const value2 = Number(statisticsSelf.invities);
							return value > 0 ? (
								<>
									<Block bold ml={2}>
										{pseudoFormat(value)}
									</Block>
									<Spacer flexBasis />
									<Block>
										<Text small>
											{t("common:invites")}{" "}
											<Text bold>
												{pseudoFormat(value2)}
											</Text>
										</Text>
									</Block>
								</>
							) : (
								value2 > 0 && (
									<>
										<Block></Block>
										<Spacer flexBasis />
										<Block>
											<Text small>
												{t("common:invites")}{" "}
												<Text bold>
													{pseudoFormat(value2)}
												</Text>
											</Text>
										</Block>
									</>
								)
							);
						}

						return (
							value > 0 && (
								<Block bold ml={2}>
									{pseudoFormat(value)}
								</Block>
							)
						);
					}
					if (route.meta.menuIconStatKey) {
						return (
							<>
								<Block flex alignCenter ml={2}>
									<Text
										style={{
											marginRight: "5px",
										}}
									>
										<VipStatus
											noPad
											status={4}
											height={14}
										/>
									</Text>
									<VipStatus noPad status={5} height={14} />
								</Block>
							</>
						);
					}
					return null;
				},
			})),
		{
			key: "d3",
			content: () => <StyledSideMenuDivider />,
		},
		{
			key: "logout",
			content: t("common:logout"),
			color: "primary",
			onClick: () => {
				logoutFn();
			},
		},
	];

	const renderMainPageNavItemsFormated = () => {
		return mainPageNavItems.map((item) => {
			let generatedPath = "";

			const isGallery = item.key === "galleries";

			if (item.key === "nearby") {
				generatedPath = isAuth
					? generateQueryPath(`${item.link.to}`, {
							city: userRegionId ? userCityId : "any",
							region: userRegionId || undefined,
							country: userCountryCode,
							distance: 50,
							sex: user.searchSex
								? [...(user?.searchSex || [])]
										.sort(searchSexCustomSort)
										.join("-")
								: "", //"woman-man-couple-transsexual",
						})
					: "/nearby";
			} else {
				generatedPath = isAuth
					? generateQueryPath(
							`${item.link.to}/${user.urlSearch}`,
							item.link.to === "/search"
								? {
										...item.link.query,
										sex: user.searchSex?.join("-") || "", //"woman-man-couple-transsexual",
									}
								: { ...item.link.query },
						)
					: user.urlSearch
						? `${item.link.to}/${user.urlSearch}`
						: `${item.link.to}`;
			}

			const generatedPathArray = generatedPath.split("?");
			const isActive = matchPath(location?.pathname, {
				path: item.path,
				exact: isGallery ? true : false,
			});

			return (
				<NavigationMenuListLink
					to={{
						pathname:
							item.link.to === "/search"
								? generatedPathArray[0]
								: item.link.to,
						state: {
							closeFiltersAfterLinkClick: true,
							forceReload: true,
						},
						search: generatedPathArray[1],
					}}
					key={item.key}
					scroll
					isActive={() => isActive}
					onClick={() => {
						if (item.key === "galleries") {
							handleGetSharedGallery("galleriesAll");
						} else if (item.key === "galleryPhotos") {
							handleGetSharedGallery("galleriesPhotos");
						} else if (item.key === "galleryVideos") {
							handleGetSharedGallery("galleriesVideos");
						} else if (item.key === "posts") {
							handleGetPosts();
						} else if (item.key === "rankings") {
							handleGetRankings();
						}
					}}
				>
					<Block flex alignCenter>
						<StyledNavigationMenuListItemIcon
							type={
								item.iconType
									? item.iconType
									: isActive
										? "fas"
										: "far"
							}
							icon={
								isActive ? item.iconActive : item.iconInactive
							}
							svgIconComponent={
								isActive
									? svgIconsList[item.iconActive]
									: svgIconsList[item.iconInactive]
							}
						/>
						<Block flex column>
							<Text bold={Boolean(isActive)}>
								{item.content &&
								typeof item.content === "function"
									? item.content({
											item,
										})
									: item.content}
							</Text>
							{item.additional && item.additional()}
						</Block>
					</Block>
				</NavigationMenuListLink>
			);
		});
	};

	const mainPageNavItemsFormated = renderMainPageNavItemsFormated();

	const isFakeUserpanel =
		matchPath(location.pathname, {
			path: "/registration-code/:login?/:id?/:hash?",
			exact: true,
			sensitive: true,
			strict: false,
		}) ||
		matchPath(location.pathname, {
			path: "/registration-phone/:login?/:id?/:hash?",
			exact: true,
			sensitive: true,
			strict: false,
		}) ||
		matchPath(location.pathname, {
			path: "/registration-verify-email",
			exact: true,
			sensitive: true,
			strict: false,
		}) ||
		matchPath(location.pathname, {
			path: "/registration-form",
			exact: true,
			sensitive: true,
			strict: false,
		});

	// #####################################################

	return (
		<StyledSideMenuCard
			ref={containerRef}
			extended={isUserpanel}
			isAuth={isAuth || isFakeUserpanel}
		>
			<CardBody fullHeight>
				<StyledSideFixed ref={contentRef}>
					{isAuth && isUserpanel && isClient && (
						<Menu
							items={profileMenuItems}
							dropdownContainer={contentRef?.current}
							userpanelMenu
							userpanelSideMenu
							customPadding="0.5rem"
						/>
					)}
					{!isUserpanel && isClient && (
						<ul>{mainPageNavItemsFormated}</ul>
					)}
					{!isUserpanel && !isClient && (
						<ul>{mainPageNavItemsFormated}</ul>
					)}
					<Spacer />
					<SideMenuServicesSection />
				</StyledSideFixed>
			</CardBody>
		</StyledSideMenuCard>
	);
};

// #####################################################

export default SideMenu;

// #####################################################
